import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable, type ColumnDef } from "@/components/shared/data-table";
import { UserForm, type UserFormValues } from "@/components/admin/users/user-form";
import type { RoleRow } from "@/components/admin/users/role-editor";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { formatDateTime } from "@/lib/format";
import { ExternalLink, Plus, Shield, Users } from "lucide-react";

interface UserRow {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  userType: "admin" | "agent" | "client";
  status: "active" | "inactive" | "pending";
  clientId: string | null;
  clientName: string | null;
  lastLoginAt: string | null;
  invitedAt: string | null;
  acceptedAt: string | null;
  createdAt: string;
  assignedRoles: RoleRow[];
}

interface ClientOption {
  id: string;
  name: string;
}

function UserActions({
  row,
  onEdit,
}: {
  row: UserRow;
  onEdit: (row: UserRow) => void;
}) {
  const { toast } = useToast();
  const resendInvite = useMutation({
    mutationFn: async () => {
      await apiRequest("POST", `/api/users/${row.id}/resend-invite`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      toast({ title: "Invite resent" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to resend invite",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const toggleStatus = useMutation({
    mutationFn: async () => {
      await apiRequest(
        "POST",
        `/api/users/${row.id}/${row.status === "inactive" ? "reactivate" : "deactivate"}`,
      );
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      toast({
        title: row.status === "inactive" ? "User reactivated" : "User deactivated",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to update user",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="flex gap-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={(event) => {
          event.stopPropagation();
          onEdit(row);
        }}
      >
        Edit
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={(event) => {
          event.stopPropagation();
          resendInvite.mutate();
        }}
      >
        Invite
      </Button>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={(event) => {
          event.stopPropagation();
          toggleStatus.mutate();
        }}
      >
        {row.status === "inactive" ? "Reactivate" : "Deactivate"}
      </Button>
    </div>
  );
}

export default function UsersPage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRow | null>(null);

  const { data: users, isLoading: usersLoading } = useQuery<UserRow[]>({
    queryKey: ["/api/users"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: roles, isLoading: rolesLoading } = useQuery<RoleRow[]>({
    queryKey: ["/api/roles"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: clients } = useQuery<ClientOption[]>({
    queryKey: ["/api/billing/clients"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const saveUser = useMutation({
    mutationFn: async (payload: UserFormValues) => {
      if (editingUser) {
        const res = await apiRequest("PATCH", `/api/users/${editingUser.id}`, payload);
        return res.json();
      }
      const res = await apiRequest("POST", "/api/users", payload);
      return res.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      setUserDialogOpen(false);
      setEditingUser(null);
      toast({ title: editingUser ? "User updated" : "User invited" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save user",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteRole = useMutation({
    mutationFn: async (roleId: string) => {
      await apiRequest("DELETE", `/api/roles/${roleId}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/roles"] });
      toast({ title: "Role deleted" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to delete role",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users ?? [];
    return (users ?? []).filter((user) =>
      [
        user.email,
        user.firstName,
        user.lastName,
        user.clientName,
        ...user.assignedRoles.map((role) => role.name),
      ]
        .filter(Boolean)
        .some((value) => value!.toLowerCase().includes(term)),
    );
  }, [search, users]);

  const userColumns: ColumnDef<UserRow>[] = [
    {
      key: "name",
      header: "User",
      cell: (row) => (
        <div>
          <p className="font-medium">
            {[row.firstName, row.lastName].filter(Boolean).join(" ") || row.email}
          </p>
          <p className="text-xs text-muted-foreground">{row.email}</p>
        </div>
      ),
    },
    {
      key: "type",
      header: "Type",
      cell: (row) => (
        <Badge variant="outline" className="capitalize">
          {row.userType}
        </Badge>
      ),
    },
    {
      key: "roles",
      header: "Roles",
      cell: (row) => (
        <div className="flex flex-wrap gap-2">
          {row.assignedRoles.map((role) => (
            <Badge key={role.id} variant="secondary">
              {role.name}
            </Badge>
          ))}
          {row.assignedRoles.length === 0 && (
            <span className="text-sm text-muted-foreground">No roles</span>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: "Status",
      cell: (row) => (
        <Badge variant={row.status === "active" ? "default" : "outline"} className="capitalize">
          {row.status}
        </Badge>
      ),
    },
    {
      key: "activity",
      header: "Last Activity",
      cell: (row) =>
        row.lastLoginAt
          ? formatDateTime(row.lastLoginAt)
          : row.invitedAt
            ? `Invited ${formatDateTime(row.invitedAt)}`
            : formatDateTime(row.createdAt),
    },
    {
      key: "actions",
      header: "Actions",
      cell: (row) => (
        <UserActions
          row={row}
          onEdit={(user) => {
            setEditingUser(user);
            setUserDialogOpen(true);
          }}
        />
      ),
    },
  ];

  const roleColumns: ColumnDef<RoleRow>[] = [
    {
      key: "name",
      header: "Role",
      cell: (row) => (
        <div>
          <p className="font-medium">{row.name}</p>
          <p className="text-xs text-muted-foreground">{row.slug}</p>
        </div>
      ),
    },
    {
      key: "audience",
      header: "Audience",
      cell: (row) => (
        <div className="flex gap-2">
          <Badge variant="outline" className="capitalize">
            {row.audience}
          </Badge>
          {row.isSystem && <Badge>System</Badge>}
        </div>
      ),
    },
    {
      key: "permissions",
      header: "Permissions",
      cell: (row) => (
        <span className="text-sm text-muted-foreground">
          {row.permissions.includes("*") ? "Full access" : `${row.permissions.length} permissions`}
        </span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      cell: (row) => (
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate(`/users/roles/${row.id}`)}
          >
            <ExternalLink className="mr-2 h-4 w-4" />
            Open
          </Button>
          {!row.isSystem && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => deleteRole.mutate(row.id)}
            >
              Delete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="User Management"
        description="Manage internal users, client users, and permission roles."
      />

      <Tabs defaultValue="users" className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="roles">Roles</TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate("/users/roles/new")}>
              <Shield className="mr-2 h-4 w-4" />
              Add Role
            </Button>
            <Button
              onClick={() => {
                setEditingUser(null);
                setUserDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </div>
        </div>

        <TabsContent value="users">
          <DataTable
            columns={userColumns}
            data={filteredUsers}
            isLoading={usersLoading}
            rowKey={(row) => row.id}
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Search users, clients, or roles..."
            emptyIcon={Users}
            emptyTitle="No users"
            emptyDescription="Create your first internal or client user."
          />
        </TabsContent>

        <TabsContent value="roles">
          <DataTable
            columns={roleColumns}
            data={roles}
            isLoading={rolesLoading}
            rowKey={(row) => row.id}
            emptyIcon={Shield}
            emptyTitle="No roles"
            emptyDescription="Create a role to bundle permissions."
          />
        </TabsContent>
      </Tabs>

      <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingUser ? "Edit user" : "Add user"}</DialogTitle>
            <DialogDescription>
              {editingUser
                ? "Update the user's profile, type, and role assignments."
                : "Create a user and send an invitation email."}
            </DialogDescription>
          </DialogHeader>
          <UserForm
            defaultValues={
              editingUser
                ? {
                    firstName: editingUser.firstName ?? "",
                    lastName: editingUser.lastName ?? "",
                    email: editingUser.email,
                    userType: editingUser.userType,
                    clientId: editingUser.clientId,
                    roleIds: editingUser.assignedRoles.map((role) => role.id),
                    status: editingUser.status,
                  }
                : undefined
            }
            availableRoles={(roles ?? []).filter(
              (role) => role.slug !== "client-partner",
            )}
            clients={clients ?? []}
            onSubmit={(values) => saveUser.mutate(values)}
            isPending={saveUser.isPending}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
